import * as React from 'react'
import './ScrollyStory.css'
import useSize from 'hooks/useSize'

const defaultRender = (width: number, height: number, progress: number) => (
  <div
    style={{
      width,
      height,
      background: '#999',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}
  >
    <div
      style={{
        color: '#fff',
        fontSize: '1rem',
        fontFamily: 'Menlo, monospace'
      }}
    >
      progress: {progress.toFixed(3)}
      <br />
      width: {width}px
      <br />
      height: {height}px
    </div>
  </div>
)

type Props = {
  length?: number
  breakpoints?: {
    minHeight: number
    width?: number
    height?: number
  }[]
  labels?: {
    content: React.ReactNode
    progress: number
  }[]
  render?: (width: number, height: number, progress: number) => JSX.Element
}

/**
 * ScrollStory
 *
 * @param render Scrollytelling component to display as render prop
 * @param length Number of screen heights to scroll through
 * @param labels A list of component/progress pairs to let scroll by during scrolling
 * @param breakpoints List of max heights at which to set the width and height of the container
 */
const ScrollyStory = ({
  render,
  length = 4,
  labels = [],
  breakpoints = [
    {
      minHeight: 700,
      width: 375
    },
    {
      minHeight: 900,
      width: 581
    }
  ],
  autoWidth
}: Props & {
  autoWidth: number
}) => {
  const stageRef = React.useRef<HTMLDivElement>(null)
  const [top, setTop] = React.useState<number>(0)
  const [windowSize, setWindowSize] = React.useState({
    width: window.innerWidth,
    height: window.innerHeight
  })
  const scrollBarWidth =
    window.innerWidth - window.document.documentElement.clientWidth

  const containerDimensions = React.useMemo(() => {
    const heightVal = Math.max(
      ...breakpoints
        .filter(b => b.minHeight <= windowSize.height)
        .map(b => b.minHeight)
    )
    return (
      breakpoints.find(b => b.minHeight === heightVal) || {
        minHeight: 0,
        width: 375
      }
    )
  }, [windowSize])

  const containerHeight = containerDimensions?.height ?? windowSize.height
  const containerWidth = containerDimensions?.width ?? autoWidth

  const stageHeight = length * windowSize.height
  const scrollHeight = stageHeight - containerHeight
  const margin = (windowSize.height - containerHeight) / 2

  const progress = Math.min(
    1,
    Math.abs(Math.min(top - margin, 0)) / scrollHeight
  )

  function handleResize() {
    setWindowSize(state =>
      // adjust the height only if the screen size increases, or if the width changes as well.
      // this leads to better results on mobile
      window.innerWidth !== state.width || window.innerHeight >= state.height
        ? {
            width: window.innerWidth,
            height: window.innerHeight
          }
        : state
    )
  }

  function handleScroll() {
    if (stageRef.current) {
      setTop(stageRef.current!.getBoundingClientRect().top)
    }
  }

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleResize)
    handleScroll()
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const getContainerStyle = React.useCallback<
    (top: number) => React.CSSProperties
  >(
    top => {
      const afterEnd = top > -scrollHeight + margin
      const afterStart = top < margin
      const isScrolling = afterStart && afterEnd
      const left = isScrolling
        ? Math.round((windowSize.width - containerWidth - scrollBarWidth) / 2)
        : Math.round((autoWidth - containerWidth) / 2)
      if (margin > 0) {
        return {
          position: isScrolling ? 'fixed' : 'absolute',
          top: isScrolling
            ? (windowSize.height - containerHeight) / 2
            : afterEnd
            ? 0
            : undefined,
          left,
          bottom: isScrolling ? undefined : 0
        }
      } else {
        return {
          position: 'sticky',
          top: 0,
          marginLeft: left
        }
      }
    },
    [windowSize, scrollBarWidth, containerWidth, containerHeight, margin]
  )

  // console.group(progress.toFixed(2))
  // console.log('margin', margin)
  // console.log('containerDimensions', containerDimensions)
  // console.log('containerWidth', containerWidth)
  // console.log('containerHeight', containerHeight)
  // console.log('windowSize.width', windowSize.width)
  // console.log('scrollBarWidth', scrollBarWidth)
  // console.log('windowSize.height', windowSize.height)
  // console.log('getContainerStyle(progress)', getContainerStyle(progress))
  // console.groupEnd()

  return (
    <>
      <div
        ref={stageRef}
        className='stage'
        style={{
          height: stageHeight,
          width: containerWidth,
          position: 'relative'
        }}
      >
        <div
          className='labels'
          style={{
            height: stageHeight,
            width: containerWidth,
            position: 'absolute',
            top: 0,
            left: (autoWidth - containerWidth) / 2,
            zIndex: 10
          }}
        >
          {labels.map((l, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                top: l.progress * stageHeight,
                opacity: progress > l.progress ? 1 : 0,
                transition: `opacity 0.2s ease-in`
              }}
            >
              {l.content}
            </div>
          ))}
        </div>
        <div className='container' style={getContainerStyle(top)}>
          {render
            ? render(containerWidth, containerHeight, progress)
            : defaultRender(containerWidth, containerHeight, progress)}
        </div>
      </div>
    </>
  )
}

const Wrapper = (props: Props) => {
  const ref = React.useRef(null)
  const size = useSize(ref)

  return (
    <div ref={ref}>
      {size ? <ScrollyStory {...props} autoWidth={size.width} /> : null}
    </div>
  )
}

export default Wrapper
